/**
 * The listing facts GA4 gets as event parameters: city_slug, price_range and
 * property_type (GA4 gap list from Measurement, 2026-10-22). view_listing
 * sends all three; save_search sends property_type from the saved filter.
 *
 * Values are short stable slugs so a GA4 dimension stays low-cardinality and
 * reads the same across events:
 *
 *   price_range    the months-of-supply price tier (SUPPLY_TIERS in
 *                  lib/market-report/bands.ts), the site's one price ladder.
 *                  None for a lease listing: its ListPrice is rent.
 *   property_type  the MLS PropertyType class (A-H, lib/property-type.ts).
 */

import { bandIdx, SUPPLY_TIERS } from '@/lib/market-report/bands'
import { listingPriceIsLeaseRate } from '@/lib/listing/publish-listing-figure'
import { propertyTypeFilterToCodes } from '@/lib/property-type'

/** MLS PropertyType code to the GA4 property_type value. */
export const GA4_PROPERTY_TYPE_BY_CODE: Readonly<Record<string, string>> = {
  A: 'residential',
  B: 'manufactured',
  C: 'multi_family',
  D: 'land',
  E: 'farm',
  F: 'commercial',
  G: 'commercial_lease',
  H: 'business_opportunity',
}

/** GA4 property_type for a raw MLS code; null when missing or unknown. */
export function ga4PropertyType(code: string | null | undefined): string | null {
  const c = (code ?? '').trim().toUpperCase()
  return GA4_PROPERTY_TYPE_BY_CODE[c] ?? null
}

/**
 * GA4 property_type for a search filter value ('A', 'Land', 'multi-family').
 * Null for no filter or a value that spans several classes (legacy
 * 'Residential' is A+B+C), so the dimension never mixes vocabularies.
 */
export function ga4PropertyTypeFromFilter(value: string | null | undefined): string | null {
  const codes = propertyTypeFilterToCodes(value)
  return codes?.length === 1 ? ga4PropertyType(codes[0]) : null
}

/** The SUPPLY_TIERS key a sale price falls in; null for no price. */
export function ga4PriceRange(price: number | null | undefined): string | null {
  const idx = bandIdx(price)
  if (idx == null) return null
  return SUPPLY_TIERS.find((t) => idx >= t.from && idx <= t.to)?.key ?? null
}

export type ListingGa4Params = {
  city_slug?: string
  price_range?: string
  property_type?: string
}

/** view_listing's listing facts, from the data the listing page already has. */
export function listingGa4Params(input: {
  citySlug?: string | null
  price?: number | null
  propertyType?: string | null
}): ListingGa4Params {
  const out: ListingGa4Params = {}
  const citySlug = input.citySlug?.trim()
  if (citySlug) out.city_slug = citySlug
  const priceRange = listingPriceIsLeaseRate(input.propertyType) ? null : ga4PriceRange(input.price)
  if (priceRange) out.price_range = priceRange
  const propertyType = ga4PropertyType(input.propertyType)
  if (propertyType) out.property_type = propertyType
  return out
}
